import { useModalLifecycle } from '../shared/useModalLifecycle'
/**
 * Team Spain v0 — Modal de Valoración Sanitaria y Disponibilidad Operativa (M6)
 * 
 * Exclusivo para el perfil Sanitario (Medic):
 * - Publica valoración operativa: Apto / Con restricciones / Pendiente de valoración / No apto.
 * - Restricciones operativas compartidas con el staff deportivo y el jugador.
 * - Fecha de próxima revisión prevista.
 * - Pestaña separada de Notas Clínicas Internas (físicamente aisladas en health_incident_clinical_notes).
 */

import React, { useState, useEffect } from 'react'
import { X, ShieldAlert, CheckCircle2, Lock, AlertCircle } from 'lucide-react'
import { formsService } from './formsService'
import { OperationalStatus, AvailabilityAssessmentItem, HealthIncidentItem } from '../../types/health'

interface MedicAssessmentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  teamId: string
  athleteId: string
  athleteName: string
  dorsalNumber?: number | null
  existingAssessment?: AvailabilityAssessmentItem | null
  activeIncident?: HealthIncidentItem | null
}

export const MedicAssessmentModal: React.FC<MedicAssessmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
  athleteId,
  athleteName,
  dorsalNumber,
  existingAssessment,
  activeIncident,
}) => {
  const [activeTab, setActiveTab] = useState<'assessment' | 'clinical_notes'>('assessment')
  const [status, setStatus] = useState<OperationalStatus>(
    existingAssessment?.status || 'available'
  )
  const [operationalRestriction, setOperationalRestriction] = useState<string>(
    existingAssessment?.operational_restriction || ''
  )
  const [sharedInstructions, setSharedInstructions] = useState<string>(
    existingAssessment?.shared_instructions || ''
  )
  const [validFrom, setValidFrom] = useState<string>(
    existingAssessment?.valid_from || new Date().toISOString().split('T')[0]
  )
  const [nextReviewDate, setNextReviewDate] = useState<string>(
    existingAssessment?.next_review_date || ''
  )

  // Notas clínicas internas (exclusivas del médico)
  const [internalNotes, setInternalNotes] = useState<string>('')
  const [diagnosis, setDiagnosis] = useState<string>('')
  const [isLoadingNotes, setIsLoadingNotes] = useState<boolean>(false)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successToast, setSuccessToast] = useState<string | null>(null)

  // Cargar notas clínicas si hay un incidente activo asociado
  useEffect(() => {
    if (!isOpen || !activeIncident) return
    setIsLoadingNotes(true)
    formsService
      .getClinicalNotes(activeIncident.id)
      .then((notes) => {
        if (notes) {
          setInternalNotes(notes.internal_notes || '')
          setDiagnosis(notes.diagnosis || '')
        }
      })
      .catch((err) => console.error('Error cargando notas clínicas:', err))
      .finally(() => setIsLoadingNotes(false))
  }, [isOpen, activeIncident])

  const isDirty =
    !successToast &&
    (status !== (existingAssessment?.status || 'available') ||
      operationalRestriction.trim() !== (existingAssessment?.operational_restriction || '') ||
      sharedInstructions.trim() !== (existingAssessment?.shared_instructions || '') ||
      internalNotes.trim() !== '' ||
      diagnosis.trim() !== '')

  const handleCloseAttempt = () => {
    if (isDirty) {
      const confirmLeave = window.confirm(
        'Tienes cambios sin guardar en la valoración médica. ¿Deseas descartarlos y salir?'
      )
      if (!confirmLeave) return
    }
    onClose()
  }

  useModalLifecycle(isOpen, handleCloseAttempt)

  if (!isOpen) return null

  const handleSaveAssessment = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMsg(null)
    setIsSubmitting(true)

    try {
      // 1. Publicar valoración operativa
      await formsService.publishAvailability(
        teamId,
        athleteId,
        status,
        status === 'available' ? undefined : operationalRestriction.trim() || undefined,
        sharedInstructions.trim() || undefined,
        validFrom,
        nextReviewDate || undefined
      )

      // 2. Si hay notas clínicas y un incidente seleccionado, guardarlas en la tabla aislada
      if (activeIncident && (internalNotes.trim() || diagnosis.trim())) {
        await formsService.saveClinicalNotes(
          activeIncident.id,
          teamId,
          internalNotes.trim(),
          diagnosis.trim() || undefined
        )
      }

      setSuccessToast('Valoración operativa publicada correctamente.')
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 800)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al publicar valoración médica.'
      setErrorMsg(msg)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className="mobile-viewport-overlay health-form-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseAttempt()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1300,
        padding: '1rem',
      }}
    >
      <div
        className="mobile-viewport-sheet"
        style={{
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.5rem',
          maxWidth: '540px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: 'var(--shadow-card)',
          border: '1px solid var(--surface-border)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>
              Valoración Médica: {athleteName}
            </h2>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {dorsalNumber !== undefined ? `#${dorsalNumber} · ` : ''}Publicación de disponibilidad operativa deportiva
            </span>
          </div>
          <button
            type="button"
            onClick={handleCloseAttempt}
            aria-label="Cerrar modal"
            style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>

        {successToast && (
          <div
            style={{
              padding: '0.65rem 0.75rem',
              backgroundColor: 'var(--status-success-bg)',
              color: 'var(--status-success-text)',
              borderRadius: '6px',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <CheckCircle2 size={16} />
            <span>{successToast}</span>
          </div>
        )}

        {errorMsg && (
          <div
            style={{
              padding: '0.65rem 0.75rem',
              backgroundColor: 'var(--status-danger-bg)',
              color: 'var(--status-danger-text)',
              borderRadius: '6px',
              fontSize: '0.82rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1rem',
            }}
          >
            <AlertCircle size={16} />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Pestañas de valoración vs notas clínicas */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', borderBottom: '1px solid var(--surface-border)', paddingBottom: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setActiveTab('assessment')}
            className={`btn ${activeTab === 'assessment' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem' }}
          >
            <ShieldAlert size={15} style={{ marginRight: '0.3rem' }} />
            Disponibilidad Operativa (Pública para staff)
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('clinical_notes')}
            className={`btn ${activeTab === 'clinical_notes' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: '0.78rem', padding: '0.35rem 0.75rem' }}
          >
            <Lock size={14} style={{ marginRight: '0.3rem', color: 'var(--brand-yellow)' }} />
            Notas Clínicas Internas (Solo Sanitario)
          </button>
        </div>

        <form onSubmit={handleSaveAssessment}>
          {activeTab === 'assessment' ? (
            <div>
              {/* Estado de disponibilidad */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.4rem' }}>
                  Estado de disponibilidad operativa deportiva *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                  {[
                    { id: 'available', label: 'Apto (Available)', desc: 'Participación deportiva completa' },
                    { id: 'limited', label: 'Con restricciones (Limited)', desc: 'Participación adaptada con límites' },
                    { id: 'needs_assessment', label: 'Pendiente de valoración', desc: 'Requiere exploración previa' },
                    { id: 'unavailable', label: 'No apto (Unavailable)', desc: 'Baja deportiva temporal' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setStatus(item.id as OperationalStatus)}
                      style={{
                        padding: '0.6rem',
                        textAlign: 'left',
                        borderRadius: '8px',
                        border: status === item.id ? '2px solid var(--brand-red)' : '1px solid var(--surface-border)',
                        backgroundColor: status === item.id ? 'var(--brand-red-subtle)' : 'var(--surface-sunken)',
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-main)' }}>
                        {item.label}
                      </div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                        {item.desc}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Restricciones prácticas operativas para el cuerpo técnico */}
              {status !== 'available' && (
                <div style={{ marginBottom: '1.1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                    Restricciones operativas (visibles para entrenadores) *
                  </label>
                  <textarea
                    rows={2}
                    required={true}
                    placeholder="Ej. Sin sprints máximos ni cambios bruscos de dirección. Solo drills técnicos estáticos y trabajo lineal."
                    value={operationalRestriction}
                    onChange={(e) => setOperationalRestriction(e.target.value)}
                    className="input"
                    style={{ width: '100%', fontSize: '0.82rem' }}
                  />
                </div>
              )}

              {/* Indicaciones compartidas con el jugador y staff */}
              <div style={{ marginBottom: '1.1rem' }}>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                  Indicaciones compartidas con el deportista
                </label>
                <textarea
                  rows={2}
                  placeholder="Ej. Calentamiento prolongado con fisioterapeuta antes de entrar a campo. Aplicar hielo 15 min al finalizar."
                  value={sharedInstructions}
                  onChange={(e) => setSharedInstructions(e.target.value)}
                  className="input"
                  style={{ width: '100%', fontSize: '0.82rem' }}
                />
              </div>

              {/* Fechas de vigencia y próxima revisión */}
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                    Válido desde *
                  </label>
                  <input
                    type="date"
                    required
                    value={validFrom}
                    onChange={(e) => setValidFrom(e.target.value)}
                    className="input"
                    style={{ width: '100%', fontSize: '0.82rem' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                    Próxima revisión prevista
                  </label>
                  <input
                    type="date"
                    value={nextReviewDate}
                    onChange={(e) => setNextReviewDate(e.target.value)}
                    className="input"
                    style={{ width: '100%', fontSize: '0.82rem' }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div
                style={{
                  padding: '0.75rem',
                  backgroundColor: 'rgba(234, 179, 8, 0.08)',
                  borderRadius: '8px',
                  border: '1px solid var(--brand-yellow)',
                  marginBottom: '1rem',
                  fontSize: '0.78rem',
                  lineHeight: 1.4,
                }}
              >
                <strong>Confidencialidad Médica Estricta:</strong> Esta información se guarda en la tabla físicamente aislada{' '}
                <code>health_incident_clinical_notes</code>. Únicamente los usuarios con rol <strong>medic</strong> tienen acceso RLS. Ni el jugador ni los entrenadores pueden ver este contenido.
              </div>

              {isLoadingNotes ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Cargando notas clínicas previas...
                </div>
              ) : activeIncident ? (
                <div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                      Diagnóstico clínico / Sospecha diagnóstica
                    </label>
                    <input
                      type="text"
                      placeholder="Ej. Distensión de semitendinoso derecho grado I, tendinopatía rotuliana..."
                      value={diagnosis}
                      onChange={(e) => setDiagnosis(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.82rem' }}
                    />
                  </div>

                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, marginBottom: '0.3rem' }}>
                      Notas clínicas internas y evolución
                    </label>
                    <textarea
                      rows={4}
                      placeholder="Hallazgos en exploración, maniobras de provocación, pauta ecográfica, evolución objetiva..."
                      value={internalNotes}
                      onChange={(e) => setInternalNotes(e.target.value)}
                      className="input"
                      style={{ width: '100%', fontSize: '0.82rem' }}
                    />
                  </div>
                </div>
              ) : (
                <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.82rem' }}>
                  No hay un incidente activo específico seleccionado para asociar notas clínicas. La valoración de disponibilidad operativa puede publicarse independientemente.
                </div>
              )}
            </div>
          )}

          {/* Botones de acción */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSubmitting}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Publicando...' : 'Publicar disponibilidad operativa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
